import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Alert, AlertTitle, Link } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers";
import {
  DatasetsList,
  type DatasetRow,
} from "@/shared/components/datasets/DatasetsList";
import { EventsList } from "@/shared/components/events";
import { FilesList } from "@/shared/components/files";
import { SelectionOption } from "@/shared/components/ResourceView";
import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";
import { EventRecord, type EventQueryScope } from "@/shared/domain/events";
import { FileRecord, type FilesQueryScope } from "@/shared/domain/files";
import { Condition } from "@/types";

import styles from "./CollectionItemsTab.module.css";
import { useCollectionOutletContext } from "./useCollectionOutletContext";

export const CollectionItemsTab: React.FC = () => {
  const collection = useCollectionOutletContext();
  const { goto } = useNavigation();

  const { mutate: updateCollection, error } = useUpdateCollection();

  const collectionConditions: Condition[] = React.useMemo(
    () => [
      {
        field: "collection.collection_id",
        comparator: "EQUALS" as const,
        value: collection.collection_id,
      },
    ],
    [collection.collection_id],
  );

  const eventScope: EventQueryScope = React.useMemo(
    () => ({ type: "collection", id: collection.collection_id }),
    [collection.collection_id],
  );

  const fileScope: FilesQueryScope = React.useMemo(
    () => ({ type: "collection", id: collection.collection_id }),
    [collection.collection_id],
  );

  const datasetSelectionOptions: SelectionOption<DatasetRow>[] = React.useMemo(
    () => [
      {
        name: "Remove from Collection",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        action: ({ selected }: { selected: DatasetRow[] }) => {
          updateCollection({
            collectionId: collection.collection_id,
            resourceOwnerId: collection.org_id,
            updates: {
              remove_resources: selected.map((d) => ({
                resource_type: "dataset" as const,
                resource_id: d.dataset_id,
              })),
            },
          });
        },
      },
    ],
    [collection.collection_id, collection.org_id, updateCollection],
  );

  const eventSelectionOptions: SelectionOption<EventRecord>[] = React.useMemo(
    () => [
      {
        name: "Remove from Collection",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        action: ({ selected }: { selected: EventRecord[] }) => {
          updateCollection({
            collectionId: collection.collection_id,
            resourceOwnerId: collection.org_id,
            updates: {
              remove_resources: selected.map((e: EventRecord) => ({
                resource_type: "event" as const,
                resource_id: e.event_id,
              })),
            },
          });
        },
      },
    ],
    [collection.collection_id, collection.org_id, updateCollection],
  );

  const fileSelectionOptions: SelectionOption<FileRecord>[] = React.useMemo(
    () => [
      {
        name: "Remove from Collection",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        action: ({ selected }) => {
          updateCollection({
            collectionId: collection.collection_id,
            resourceOwnerId: collection.org_id,
            updates: {
              remove_resources: selected.map((f) => ({
                resource_type: "file" as const,
                resource_id: f.file_id,
              })),
            },
          });
        },
      },
      {
        name: "Visualize",
        icon: <VisibilityIcon fontSize="small" />,
        action: ({ selected }) => {
          goto.visualize({
            dataSource: { files: selected.map((f) => f.file_id) },
          });
        },
      },
    ],
    [collection.collection_id, collection.org_id, updateCollection, goto],
  );

  const isEmpty =
    (collection.resources[collection.resource_type]?.length ?? 0) === 0;

  if (isEmpty) {
    return (
      <Alert severity="info">
        <AlertTitle>Empty Collection</AlertTitle>
        {collection.resource_type === "dataset" && (
          <>
            There are no items in this collection yet. You can add{" "}
            <Link href="/datasets" underline="hover">
              datasets
            </Link>{" "}
            to it.
          </>
        )}
        {collection.resource_type === "file" && (
          <>
            There are no items in this collection yet. You can add individual
            files to it.
          </>
        )}
        {collection.resource_type === "event" && (
          <>
            There are no items in this collection yet. You can add individual
            events to it.
          </>
        )}
      </Alert>
    );
  }

  return (
    <>
      {error && (
        <Alert severity="error" style={{ marginBottom: "var(--spacing-xs)" }}>
          {error.message}
        </Alert>
      )}

      {collection.resource_type === "dataset" && (
        <DatasetsList
          conditions={collectionConditions}
          selectionOptions={datasetSelectionOptions}
        />
      )}

      {collection.resource_type === "file" && (
        <FilesList scope={fileScope} selectionOptions={fileSelectionOptions} />
      )}

      {collection.resource_type === "event" && (
        <div className={styles.eventsTableWrapper}>
          <EventsList
            scope={eventScope}
            selectionOptions={eventSelectionOptions}
          />
        </div>
      )}
    </>
  );
};
